/**
 * Zuhe Popups - JavaScript Controller
 * Shopify Horizon Theme
 * 
 * Gestiona popups de tipo Imagen y Formulario Brevo (Newsletter).
 * Cumple con accesibilidad WCAG 2.1 AA, contención de foco (focus trap),
 * diseño horizontal responsivo en escritorio / vertical en móvil,
 * normalización exacta de rutas, almacenamiento seguro, analítica opcional,
 * diagnóstico de bloques e integración completa con el ciclo de vida de Shopify Theme Editor.
 */

(function () {
  'use strict';

  // Evitar ejecuciones duplicadas si el script se incluye múltiples veces
  if (window.ZuhePopupsInitialized) {
    return;
  }
  window.ZuhePopupsInitialized = true;

  /**
   * Almacenamiento seguro con control de excepciones (Private Browsing, cookies bloqueadas, cuota).
   */
  const StorageSafe = {
    getItem: function (type, key) {
      try {
        const storage = type === 'session' ? window.sessionStorage : window.localStorage;
        return storage ? storage.getItem(key) : null;
      } catch (e) {
        console.warn('[ZuhePopups] Storage no accesible (lectura):', e);
        return null;
      }
    },
    setItem: function (type, key, value) {
      try {
        const storage = type === 'session' ? window.sessionStorage : window.localStorage;
        if (storage) {
          storage.setItem(key, value);
        }
      } catch (e) {
        console.warn('[ZuhePopups] Storage no accesible (escritura):', e);
      }
    }
  };

  /**
   * Normalización rigurosa de rutas URL:
   * - Elimina parámetros de consulta (?...) y fragmentos (#...).
   * - Elimina barras finales (trailing slash), excepto en la raíz exacta ('/').
   * - Convierte a minúsculas y garantiza barra inicial.
   */
  function normalizePath(rawPath) {
    if (!rawPath || typeof rawPath !== 'string') return '/';
    let clean = rawPath.split('?')[0].split('#')[0].trim().toLowerCase();
    if (!clean.startsWith('/')) {
      clean = '/' + clean;
    }
    // Eliminar barras finales sucesivas si la longitud es mayor a 1
    if (clean.length > 1) {
      clean = clean.replace(/\/+$/, '');
    }
    return clean || '/';
  }

  /**
   * Verifica exclusiones automáticas del sistema (no configurables):
   * /cart, /checkout, /checkouts y /account (incluyendo prefijos de idioma como /es/cart).
   */
  function isSystemExcluded(pathname) {
    const path = normalizePath(pathname);
    const systemRegex = /^(\/[a-z]{2}(-[a-z]{2,4})?)?\/(cart|checkout|checkouts|account)($|\/)/i;
    return systemRegex.test(path);
  }

  /**
   * Generación segura de URLs con UTM preservando parámetros existentes y hash.
   */
  function buildUtmUrl(rawUrl, utmSource, utmMedium, utmCampaign) {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
    try {
      const isAbsolute = rawUrl.startsWith('http://') || rawUrl.startsWith('https://');
      const dummyOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'https://zuhenatural.com';
      const parsed = new URL(rawUrl, dummyOrigin);
      parsed.searchParams.set('utm_source', utmSource || 'web');
      parsed.searchParams.set('utm_medium', utmMedium || 'popup');
      parsed.searchParams.set('utm_campaign', utmCampaign || 'promo_popup');
      return isAbsolute ? parsed.href : (parsed.pathname + parsed.search + parsed.hash);
    } catch (e) {
      return rawUrl;
    }
  }

  /**
   * Envío seguro de eventos analíticos al dataLayer.
   */
  function sendAnalyticsEvent(eventName, payload) {
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode) {
      // En modo diseño del editor de temas no enviamos analítica real
      return;
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch (err) {
      console.warn('[ZuhePopups] Error al emitir evento al dataLayer:', err);
    }
  }

  /**
   * Generador de documento HTML aislado para el formulario Brevo.
   * Expuesto para permitir validaciones directas y renderizado limpio sin fugas.
   */
  function generateBrevoDocContent(config) {
    const action = config.action || '';
    const heading = config.heading || 'Suscríbete y obtén 10% OFF';
    const subheading = config.subheading || 'Únete a nuestra comunidad y recibe promociones exclusivas.';
    const buttonText = config.buttonText || 'Suscribirme';
    const imageUrl = config.imageUrl || '';
    const imageSrcset = config.imageSrcset || '';
    const imageAlt = config.imageAlt || 'Promoción Zuhe Natural';
    const imageWidth = config.imageWidth || '';
    const imageHeight = config.imageHeight || '';
    const hasImage = Boolean(imageUrl);
    const isMobile = typeof config.isMobile === 'boolean'
      ? config.isMobile
      : (typeof window !== 'undefined' ? window.innerWidth < 750 : false);

    const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[character]);

    const imageMarkup = hasImage
      ? `<div class="zuhe-brevo-col-media">
          <figure class="zuhe-brevo-media">
            <img
              src="${escapeHtml(imageUrl)}"
              ${imageSrcset ? `srcset="${escapeHtml(imageSrcset)}" sizes="(max-width: 480px) 92vw, 360px"` : ''}
              ${imageWidth ? `width="${escapeHtml(imageWidth)}"` : ''}
              ${imageHeight ? `height="${escapeHtml(imageHeight)}"` : ''}
              alt="${escapeHtml(imageAlt)}"
              class="zuhe-brevo-media__img"
              loading="eager"
            >
          </figure>
        </div>`
      : '';

    const headingMarkup = heading
      ? `<header class="zuhe-brevo-header">
          <h3 class="zuhe-brevo-title">${escapeHtml(heading)}</h3>
          ${subheading ? `<p class="zuhe-brevo-subtitle">${escapeHtml(subheading)}</p>` : ''}
        </header>`
      : '';

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Suscripción Zuhe Natural</title>
  <link rel="stylesheet" href="https://sibforms.com/forms/end-form/build/sib-styles.css">
  <style>
    * {
      box-sizing: border-box;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: transparent;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #4C5D5C;
      -webkit-font-smoothing: antialiased;
    }
    .zuhe-brevo-layout {
      width: 100%;
      margin: 0 auto;
      background: #ffffff;
      box-sizing: border-box;
    }
    /* Estructura con Imagen: 2 columnas en escritorio (imagen izquierda, form derecha) */
    .zuhe-brevo-layout--has-image {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      min-height: 380px;
    }
    .zuhe-brevo-col-media {
      flex: 0 0 42%;
      max-width: 42%;
      position: relative;
      overflow: hidden;
      background: #f4f6f4;
      display: flex;
      align-items: stretch;
    }
    .zuhe-brevo-media {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      display: flex;
    }
    .zuhe-brevo-media__img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
    .zuhe-brevo-col-form {
      flex: 1 1 58%;
      max-width: 58%;
      padding: 26px 28px 24px 22px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      box-sizing: border-box;
    }

    /* En escritorio garantizado: NUNCA en columna, SIEMPRE fila (imagen izquierda, form derecha) */
    body.is-desktop-device .zuhe-brevo-layout--has-image {
      flex-direction: row !important;
    }
    body.is-desktop-device .zuhe-brevo-col-media {
      flex: 0 0 42% !important;
      max-width: 42% !important;
      width: 42% !important;
      height: auto !important;
      max-height: none !important;
    }
    body.is-desktop-device .zuhe-brevo-col-form {
      flex: 1 1 58% !important;
      max-width: 58% !important;
      width: 58% !important;
    }

    /* Estructura sin Imagen: 1 columna centrada limpia */
    .zuhe-brevo-layout--no-image {
      max-width: 420px;
      padding: 36px 30px 30px;
      text-align: center;
    }
    .zuhe-brevo-layout--no-image .zuhe-brevo-col-form {
      padding: 0;
      max-width: 100%;
    }
    .zuhe-brevo-layout--no-image .zuhe-brevo-header {
      text-align: center;
    }
    .zuhe-brevo-layout--no-image .form__label {
      text-align: left;
    }
    .zuhe-brevo-layout--no-image .sib-form-block {
      text-align: center;
    }

    /* Encabezados */
    .zuhe-brevo-header {
      margin-bottom: 14px;
      text-align: left;
    }
    .zuhe-brevo-title {
      margin: 0 0 6px 0;
      font-size: 20px;
      font-weight: 700;
      color: #1e293b;
      line-height: 1.25;
    }
    .zuhe-brevo-subtitle {
      margin: 0;
      font-size: 13px;
      color: #64748b;
      line-height: 1.4;
    }

    /* Formulario oficial Brevo */
    #sib-form-container {
      width: 100%;
    }
    #sib-container {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      padding: 0 !important;
      max-width: 100% !important;
    }
    form#sib-form {
      padding: 0;
      margin: 0;
    }
    .form__entry {
      margin-bottom: 10px;
    }
    .form__label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      margin-bottom: 4px;
      color: #4C5D5C;
      text-align: left;
    }
    .entry__field {
      position: relative;
    }
    .input {
      width: 100%;
      height: 38px;
      padding: 0 16px;
      background: #ffffff;
      border: 1px solid #C0CCD9;
      border-radius: 999px;
      font-family: Helvetica, Arial, sans-serif;
      font-size: 13px;
      font-weight: 400;
      color: #4C5D5C;
      box-sizing: border-box;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      outline: none;
    }
    .input::placeholder {
      color: #8A9694;
      opacity: 1;
    }
    .input:focus {
      border-color: #DBF984;
      box-shadow: 0 0 0 2px rgba(219, 249, 132, 0.20);
    }

    /* Botón de suscripción oficial */
    .sib-form-block {
      text-align: left;
      margin-top: 10px;
    }
    .sib-form-block__button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 145px;
      max-width: 160px;
      height: 44px;
      padding: 0 22px;
      border-radius: 999px;
      font-family: inherit;
      font-size: 15px;
      font-weight: 500;
      background-color: #DBF984;
      border: 2px solid #DBF984;
      color: #4C5D5C;
      cursor: pointer;
      transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
      box-sizing: border-box;
    }
    .sib-form-block__button:hover {
      background-color: #4C5D5C;
      border-color: #DBF984;
      color: #DBF984;
    }

    /* Mensajes de error accesibles */
    .entry__error {
      font-size: 12px;
      line-height: 1.3;
      padding: 4px 10px;
      margin-top: 4px;
      border-radius: 999px;
      background-color: #ffeded;
      color: #661d1d;
      border: 1px solid #ff4949;
      box-sizing: border-box;
    }
    .entry__error:empty {
      display: none !important;
    }
    .entry__error:not(:empty) {
      display: block !important;
    }

    /* Paneles de estado (error / éxito) */
    .sib-form-message-panel {
      display: none;
      border-radius: 10px;
      padding: 10px 14px;
      margin-bottom: 12px;
      font-size: 13px;
      line-height: 1.35;
      text-align: left;
      box-sizing: border-box;
    }
    #error-message {
      background-color: #ffeded;
      border: 1px solid #ff4949;
      color: #661d1d;
    }
    #success-message {
      background-color: #e7faf0;
      border: 1px solid #13ce66;
      color: #085229;
    }

    .input--hidden {
      display: none !important;
      visibility: hidden !important;
      position: absolute !important;
      left: -9999px !important;
    }

    /* Diseño apilado en móvil (< 480px) */
    @media screen and (max-width: 480px) {
      .zuhe-brevo-layout--has-image {
        flex-direction: column !important;
        min-height: auto;
      }
      .zuhe-brevo-col-media {
        flex: 0 0 auto !important;
        max-width: 100% !important;
        width: 100% !important;
        height: 160px !important;
        max-height: 180px !important;
      }
      .zuhe-brevo-col-form {
        flex: 1 1 auto !important;
        max-width: 100% !important;
        width: 100% !important;
        padding: 20px 20px 24px;
      }
      .zuhe-brevo-header {
        margin-bottom: 12px;
      }
      .zuhe-brevo-title {
        font-size: 19px;
      }
      .zuhe-brevo-subtitle {
        font-size: 12.5px;
      }
      .sib-form-block {
        text-align: center;
      }
      .sib-form-block__button {
        height: 46px;
        width: 100%;
        max-width: 100%;
      }
    }

    body.is-mobile-device .zuhe-brevo-layout--has-image {
      flex-direction: column !important;
      min-height: auto;
    }
    body.is-mobile-device .zuhe-brevo-col-media {
      flex: 0 0 auto !important;
      max-width: 100% !important;
      width: 100% !important;
      height: 160px !important;
      max-height: 180px !important;
    }
    body.is-mobile-device .zuhe-brevo-col-form {
      flex: 1 1 auto !important;
      max-width: 100% !important;
      width: 100% !important;
      padding: 20px 20px 24px;
    }
    body.is-mobile-device .sib-form-block__button {
      height: 46px;
      width: 100%;
      max-width: 100%;
    }
  </style>
</head>
<body class="${isMobile ? 'is-mobile-device' : 'is-desktop-device'}">
  <div class="zuhe-brevo-layout ${hasImage ? 'zuhe-brevo-layout--has-image' : 'zuhe-brevo-layout--no-image'}">
    ${imageMarkup}
    <div class="zuhe-brevo-col-form">
      ${headingMarkup}
      <div id="sib-form-container">
        <div id="error-message" class="sib-form-message-panel" role="alert" aria-live="polite">
          No hemos podido validar tu suscripción.
        </div>
        <div id="success-message" class="sib-form-message-panel" role="alert" aria-live="polite">
          Se ha realizado tu suscripción.
        </div>

        <div id="sib-container">
          <form id="sib-form" method="POST" action="${escapeHtml(action)}" data-type="subscription" novalidate>
            <div class="form__entry">
              <label for="FIRSTNAME" class="form__label">Tu nombre</label>
              <div class="entry__field">
                <input
                  class="input"
                  type="text"
                  id="FIRSTNAME"
                  name="FIRSTNAME"
                  autocomplete="given-name"
                  maxlength="200"
                  placeholder="Tu nombre"
                  required
                  aria-required="true"
                />
              </div>
              <label class="entry__error entry__error--primary" aria-live="polite"></label>
            </div>

            <div class="form__entry">
              <label for="EMAIL" class="form__label">Tu correo</label>
              <div class="entry__field">
                <input
                  class="input"
                  type="email"
                  id="EMAIL"
                  name="EMAIL"
                  autocomplete="email"
                  placeholder="Tu correo"
                  required
                  aria-required="true"
                />
              </div>
              <label class="entry__error entry__error--primary" aria-live="polite"></label>
            </div>

            <div class="sib-form-block">
              <button class="sib-form-block__button" form="sib-form" type="submit">
                ${escapeHtml(buttonText)}
              </button>
            </div>

            <input type="text" name="email_address_check" value="" class="input--hidden" tabindex="-1" aria-hidden="true" autocomplete="off">
            <input type="hidden" name="locale" value="es">
          </form>
        </div>
      </div>
    </div>
  </div>

  <script>
    window.REQUIRED_CODE_ERROR_MESSAGE = 'Elige un código de país';
    window.LOCALE = 'es';
    window.EMAIL_INVALID_MESSAGE = window.SMS_INVALID_MESSAGE = "La información que has proporcionado no es válida. Compruebe el formato del campo e inténtalo de nuevo.";
    window.REQUIRED_ERROR_MESSAGE = "Este campo no puede quedarse vacío. ";
    window.GENERIC_INVALID_MESSAGE = "La información que has proporcionado no es válida. Compruebe el formato del campo e inténtalo de nuevo.";
    window.translation = {
      common: {
        selectedList: '{quantity} lista seleccionada',
        selectedLists: '{quantity} listas seleccionadas',
        selectedOption: '{quantity} seleccionado',
        selectedOptions: '{quantity} seleccionados',
      }
    };
    var AUTOHIDE = Boolean(0);

    function reportDocSize() {
      try {
        var h = Math.ceil(document.documentElement.scrollHeight || document.body.scrollHeight || 420);
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'zuhe:brevo:resize', height: h }, '*');
        }
      } catch (e) {}
    }
    window.addEventListener('load', reportDocSize);
    window.addEventListener('resize', reportDocSize);
    if (window.ResizeObserver) {
      try {
        new ResizeObserver(reportDocSize).observe(document.body);
      } catch (e) {}
    }
  </script>
  <script defer src="https://sibforms.com/forms/end-form/build/main.js"></script>
</body>
</html>`;
  }

  /**
   * Controlador Principal de Popups Zuhe
   */
  class ZuhePopupsController {
    constructor(rootElement) {
      this.root = rootElement;
      this.sectionId = rootElement.dataset.sectionId || 'zuhe-popups';
      this.isDesignMode = rootElement.dataset.isDesignMode === 'true' ||
                          (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode === true);
      this.activePopupOverlay = null;
      this.activeTimer = null;
      this.previouslyFocusedElement = null;
      this.boundKeydownHandler = this.handleKeydown.bind(this);
      this.boundOverlayClickHandler = this.handleOverlayClick.bind(this);
      this.boundMessageHandler = this.handleWindowMessage.bind(this);
      this.boundResizeHandler = this.handleWindowResize.bind(this);

      this.init();
    }

    init() {
      // Si la página actual pertenece al sistema (cart, checkout, account), abortar de inmediato
      if (!this.isDesignMode && isSystemExcluded(window.location.pathname)) {
        return;
      }

      this.bindEvents();

      // En la tienda pública, evaluar y programar el popup ganador por orden de prioridad
      if (!this.isDesignMode) {
        this.evaluateAndSchedule();
      }
    }

    bindEvents() {
      // Manejador global de teclado (Escape y Focus Trap)
      document.addEventListener('keydown', this.boundKeydownHandler);

      // Clics en overlays y botones de cierre
      this.root.addEventListener('click', this.boundOverlayClickHandler);

      // Comunicación de redimensionado automático desde el iframe aislado
      window.addEventListener('message', this.boundMessageHandler);

      // Adaptación de layout al redimensionar ventana
      window.addEventListener('resize', this.boundResizeHandler);

      // Clics en enlaces de imagen para emitir analítica y normalizar UTM
      const links = this.root.querySelectorAll('[data-zuhe-popup-link]');
      links.forEach((link) => {
        const overlay = link.closest('.zuhe-popup-overlay');
        if (overlay && overlay.dataset.enableUtm === 'true') {
          const currentHref = link.getAttribute('href');
          const fixedHref = buildUtmUrl(
            currentHref,
            overlay.dataset.utmSource,
            overlay.dataset.utmMedium,
            overlay.dataset.utmCampaign
          );
          if (fixedHref && fixedHref !== currentHref) {
            link.setAttribute('href', fixedHref);
          }
        }

        link.addEventListener('click', () => {
          if (overlay && overlay.dataset.enableAnalytics === 'true') {
            sendAnalyticsEvent('popup_click', {
              popup_name: overlay.dataset.campaignName,
              popup_id: overlay.dataset.blockId,
              popup_url: link.getAttribute('href')
            });
          }
        });
      });
    }

    handleWindowMessage(event) {
      if (!event || !event.data) return;
      if (event.data.type === 'zuhe:brevo:resize' && typeof event.data.height === 'number') {
        const containers = this.root.querySelectorAll('.zuhe-popup__brevo-custom-container');
        containers.forEach((c) => {
          c.style.setProperty('--brevo-dynamic-height', `${event.data.height}px`);
        });
      }
    }

    handleWindowResize() {
      const isMobile = window.innerWidth < 750;
      const iframes = this.root.querySelectorAll('iframe[data-brevo-isolated="true"]');
      iframes.forEach((iframe) => {
        try {
          const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
          if (doc && doc.body) {
            if (isMobile) {
              doc.body.classList.add('is-mobile-device');
              doc.body.classList.remove('is-desktop-device');
            } else {
              doc.body.classList.add('is-desktop-device');
              doc.body.classList.remove('is-mobile-device');
            }
          }
        } catch (e) {}
      });
    }

    /**
     * Evalúa la elegibilidad de cada bloque según su orden en el DOM.
     * Selecciona únicamente el primer bloque elegible (prioridad estricta).
     * Cancela cualquier temporizador previo para evitar ejecuciones múltiples.
     */
    evaluateAndSchedule() {
      // Cancelar temporizador previo si existe
      if (this.activeTimer) {
        clearTimeout(this.activeTimer);
        this.activeTimer = null;
      }

      const overlays = Array.from(this.root.querySelectorAll('.zuhe-popup-overlay'));
      const currentPath = normalizePath(window.location.pathname);
      const isMobile = window.innerWidth < 750;
      const isDebug = Boolean(
        window.ZuhePopupsDebug ||
        (window.location && window.location.search && window.location.search.includes('zuhe_debug'))
      );

      let winningOverlay = null;
      const diagnostics = [];

      for (let i = 0; i < overlays.length; i++) {
        const overlay = overlays[i];
        const status = this.checkBlockEligibility(overlay, currentPath, isMobile);
        diagnostics.push({
          order: i + 1,
          id: overlay.dataset.blockId,
          type: overlay.dataset.blockType,
          name: overlay.dataset.campaignName,
          eligible: status.eligible,
          reason: status.reason
        });

        if (status.eligible && !winningOverlay) {
          winningOverlay = overlay;
          const delaySeconds = Math.max(0, parseInt(overlay.dataset.delay, 10) || 0);
          this.activeTimer = setTimeout(() => {
            this.openPopup(overlay);
          }, delaySeconds * 1000);

          // Máximo un popup por carga de página: detener tras hallar el primero
          break;
        }
      }

      if (isDebug) {
        console.groupCollapsed(`[ZuhePopups] Diagnóstico de evaluación (${this.sectionId})`);
        console.table(diagnostics);
        if (winningOverlay) {
          console.log(`🎯 Popup seleccionado: "${winningOverlay.dataset.campaignName}" (Delay: ${winningOverlay.dataset.delay || 0}s)`);
        } else {
          console.log('ℹ️ Ningún popup elegible para mostrar en esta carga.');
        }
        console.groupEnd();
      }

      return diagnostics;
    }

    /**
     * Valida si un bloque cumple todos los criterios de visualización,
     * retornando el estado booleano y la razón detallada.
     */
    checkBlockEligibility(overlay, currentPath, isMobile) {
      // 1. Activación
      if (overlay.dataset.enabled !== 'true') {
        return { eligible: false, reason: 'Desactivado (enabled: false)' };
      }

      // 2. Si el bloque no está configurado (ej: imagen sin archivo o Brevo sin action/URL), omitir
      if (overlay.dataset.unconfigured === 'true') {
        const type = overlay.dataset.blockType;
        const msg = type === 'image'
          ? 'Imagen no configurada (falta seleccionar imagen de escritorio)'
          : 'Brevo no configurado (falta Action URL o URL de iframe)';
        return { eligible: false, reason: msg };
      }

      // 3. Dispositivo
      const deviceTarget = overlay.dataset.device || 'all';
      if (deviceTarget === 'desktop' && isMobile) {
        return { eligible: false, reason: 'Solo escritorio (vista móvil detectada)' };
      }
      if (deviceTarget === 'mobile' && !isMobile) {
        return { eligible: false, reason: 'Solo móvil (vista escritorio detectada)' };
      }

      // 4. Exclusiones de ruta específicas del bloque
      const rawExcluded = overlay.dataset.pathsExcluded || '';
      if (rawExcluded.trim()) {
        const excludedList = rawExcluded.split('\n').map(normalizePath).filter(Boolean);
        if (excludedList.includes(currentPath)) {
          return { eligible: false, reason: `Ruta excluida en el bloque (${currentPath})` };
        }
      }

      // 5. Alcance y rutas incluidas
      const displayScope = overlay.dataset.displayScope || 'all';
      if (displayScope === 'home') {
        if (currentPath !== '/') {
          return { eligible: false, reason: 'Solo página de inicio (ruta actual no es /)' };
        }
      } else if (displayScope === 'custom') {
        const rawIncluded = overlay.dataset.pathsIncluded || '';
        const includedList = rawIncluded.split('\n').map(normalizePath).filter(Boolean);
        if (!includedList.includes(currentPath)) {
          return { eligible: false, reason: `Ruta no incluida en lista personalizada (${currentPath})` };
        }
      }

      // 6. Frecuencia de aparición
      const frequency = overlay.dataset.frequency || '7days';
      if (frequency !== 'always') {
        const blockId = overlay.dataset.blockId;
        const campaignId = overlay.dataset.campaignId || 'default';
        const storageKey = `zuhe_popup_${this.sectionId}_${blockId}_${campaignId}`;

        if (frequency === 'session') {
          const sessionSeen = StorageSafe.getItem('session', storageKey);
          if (sessionSeen) {
            return { eligible: false, reason: 'Frecuencia por sesión ya cumplida en esta pestaña' };
          }
        } else if (frequency === '1day' || frequency === '7days') {
          const lastSeenStr = StorageSafe.getItem('local', storageKey);
          if (lastSeenStr) {
            const lastSeenTime = parseInt(lastSeenStr, 10);
            if (!isNaN(lastSeenTime)) {
              const maxAgeMs = frequency === '1day' ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
              const elapsed = Date.now() - lastSeenTime;
              if (elapsed < maxAgeMs) {
                const remainingHours = Math.ceil((maxAgeMs - elapsed) / (1000 * 60 * 60));
                return { eligible: false, reason: `Frecuencia ${frequency} activa (restan ~${remainingHours}h)` };
              }
            }
          }
        }
      }

      return { eligible: true, reason: 'Elegible para mostrar' };
    }

    /**
     * Compatibilidad hacia atrás para tests unitarios y controladores.
     */
    isBlockEligible(overlay, currentPath, isMobile) {
      return this.checkBlockEligibility(overlay, currentPath, isMobile).eligible;
    }

    /**
     * Abre un popup específico:
     * - Lazy-loading del iframe Brevo (si aplica)
     * - Registro de frecuencia en storage
     * - Gestión segura de foco accesible
     * - Bloqueo de scroll del body
     * - Emisión de analítica al dataLayer
     */
    openPopup(overlay, isEditorPreview = false) {
      if (!overlay) return;

      // Evitar reabrir el popup si ya está visible y activo
      if (this.activePopupOverlay === overlay && overlay.classList.contains('is-active')) {
        return;
      }

      // Cancelar cualquier temporizador pendiente al abrir manualmente o en editor
      if (this.activeTimer) {
        clearTimeout(this.activeTimer);
        this.activeTimer = null;
      }

      // Si ya hay otro popup abierto, cerrarlo primero de forma limpia
      if (this.activePopupOverlay && this.activePopupOverlay !== overlay) {
        this.closePopup(this.activePopupOverlay, 'replaced');
      }

      // Guardar el elemento previamente enfocado solo si no estábamos dentro de un popup
      if (!this.previouslyFocusedElement || !this.previouslyFocusedElement.closest('.zuhe-popup-overlay')) {
        this.previouslyFocusedElement = document.activeElement;
      }

      this.activePopupOverlay = overlay;

      // Carga diferida del iframe Brevo externo (si aplica)
      const iframe = overlay.querySelector('iframe[data-src]');
      if (iframe && !iframe.getAttribute('src')) {
        const srcUrl = iframe.getAttribute('data-src');
        if (srcUrl) {
          iframe.setAttribute('src', srcUrl);
        }
      }

      // Carga diferida del formulario Brevo aislado con diseño Zuhe
      const isolatedFrame = overlay.querySelector('iframe[data-brevo-isolated="true"]');
      if (isolatedFrame && !isolatedFrame.dataset.loaded) {
        isolatedFrame.dataset.loaded = 'true';
        this.renderIsolatedBrevoForm(isolatedFrame);
      }

      // Registro de frecuencia: SIEMPRE al abrir, NUNCA al cerrar
      if (!this.isDesignMode && !isEditorPreview) {
        const frequency = overlay.dataset.frequency || '7days';
        const blockId = overlay.dataset.blockId;
        const campaignId = overlay.dataset.campaignId || 'default';
        const storageKey = `zuhe_popup_${this.sectionId}_${blockId}_${campaignId}`;

        if (frequency === 'session') {
          StorageSafe.setItem('session', storageKey, '1');
        } else if (frequency === '1day' || frequency === '7days') {
          StorageSafe.setItem('local', storageKey, Date.now().toString());
        }
      }

      // Mostrar el elemento en el DOM
      overlay.style.display = 'flex';
      overlay.setAttribute('aria-hidden', 'false');

      // Forzar reflow para transición CSS
      void overlay.offsetWidth;
      overlay.classList.add('is-active');

      // Bloquear desplazamiento del fondo (body)
      document.body.classList.add('zuhe-popup-open');

      // Enfoque accesible inicial: botón de cierre
      const closeButton = overlay.querySelector('[data-zuhe-popup-close]');
      if (closeButton) {
        closeButton.focus();
      } else {
        const dialog = overlay.querySelector('.zuhe-popup');
        if (dialog) dialog.focus();
      }

      // Analítica (dataLayer)
      if (!isEditorPreview && overlay.dataset.enableAnalytics === 'true') {
        sendAnalyticsEvent('popup_view', {
          popup_name: overlay.dataset.campaignName,
          popup_id: overlay.dataset.blockId,
          popup_type: overlay.dataset.blockType
        });
      }
    }

    /**
     * Construye y renderiza el formulario oficial Brevo con diseño horizontal/vertical Zuhe.
     */
    renderIsolatedBrevoForm(iframe) {
      const isMobile = typeof window !== 'undefined' ? window.innerWidth < 750 : false;
      const docContent = generateBrevoDocContent({
        action: iframe.dataset.action,
        heading: iframe.dataset.heading,
        subheading: iframe.dataset.subheading,
        buttonText: iframe.dataset.buttonText,
        imageUrl: iframe.dataset.imageUrl,
        imageSrcset: iframe.dataset.imageSrcset,
        imageAlt: iframe.dataset.imageAlt,
        imageWidth: iframe.dataset.imageWidth,
        imageHeight: iframe.dataset.imageHeight,
        isMobile: isMobile
      });

      try {
        iframe.srcdoc = docContent;
      } catch (e) {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (doc) {
          doc.open();
          doc.write(docContent);
          doc.close();
        }
      }
    }

    /**
     * Cierra el popup activo:
     * - Oculta overlay con transición
     * - Restaura scroll del body
     * - Devuelve el foco al elemento anterior
     * - Emite evento de cierre al dataLayer
     */
    closePopup(overlay, closeMethod = 'unknown') {
      if (!overlay) return;

      overlay.classList.remove('is-active');
      overlay.setAttribute('aria-hidden', 'true');

      // Tras la transición CSS, ocultar con display: none
      setTimeout(() => {
        if (!overlay.classList.contains('is-active')) {
          overlay.style.display = 'none';
        }
      }, 250);

      document.body.classList.remove('zuhe-popup-open');

      // Devolver foco al elemento original
      if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
        try {
          this.previouslyFocusedElement.focus();
        } catch (e) {
          // Elemento ya no existe en el DOM
        }
      }
      this.previouslyFocusedElement = null;

      // Emitir evento de analítica
      if (overlay.dataset.enableAnalytics === 'true' && closeMethod !== 'replaced') {
        sendAnalyticsEvent('popup_close', {
          popup_name: overlay.dataset.campaignName,
          popup_id: overlay.dataset.blockId,
          popup_close_method: closeMethod
        });
      }

      if (this.activePopupOverlay === overlay) {
        this.activePopupOverlay = null;
      }
    }

    /**
     * Manejador de clics: botón de cierre y clic fuera (overlay).
     */
    handleOverlayClick(event) {
      const closeButton = event.target.closest('[data-zuhe-popup-close]');
      if (closeButton) {
        const overlay = closeButton.closest('.zuhe-popup-overlay');
        this.closePopup(overlay, 'button');
        return;
      }

      // Si el clic fue directamente en el overlay (fuera del cuadro de diálogo)
      if (event.target.classList.contains('zuhe-popup-overlay')) {
        const overlay = event.target;
        if (overlay.dataset.closeOnOverlay === 'true') {
          this.closePopup(overlay, 'overlay');
        }
      }
    }

    /**
     * Manejador de teclado para accesibilidad:
     * - Tecla Escape (cierra popup cuando el foco está en el documento anfitrión)
     * - Tecla Tab (contención de foco / focus trap dentro del modal)
     */
    handleKeydown(event) {
      if (!this.activePopupOverlay || !this.activePopupOverlay.classList.contains('is-active')) {
        return;
      }

      // 1. Escape
      if (event.key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
        this.closePopup(this.activePopupOverlay, 'escape');
        return;
      }

      // 2. Focus Trap (Tab y Shift+Tab)
      if (event.key === 'Tab' || event.keyCode === 9) {
        const dialog = this.activePopupOverlay.querySelector('.zuhe-popup');
        if (!dialog) return;

        const focusableSelectors = [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'iframe',
          '[tabindex]:not([tabindex="-1"])'
        ].join(', ');

        const focusableElements = Array.from(dialog.querySelectorAll(focusableSelectors)).filter(
          (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
        );

        if (focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement || document.activeElement === dialog) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }

    /**
     * Diagnóstico estructurado de todos los bloques gestionados por este controlador.
     */
    diagnose() {
      const overlays = Array.from(this.root.querySelectorAll('.zuhe-popup-overlay'));
      const currentPath = normalizePath(window.location.pathname);
      const isMobile = window.innerWidth < 750;

      return overlays.map((overlay, index) => {
        const status = this.checkBlockEligibility(overlay, currentPath, isMobile);
        return {
          order: index + 1,
          blockId: overlay.dataset.blockId,
          type: overlay.dataset.blockType,
          campaignName: overlay.dataset.campaignName,
          enabled: overlay.dataset.enabled === 'true',
          unconfigured: overlay.dataset.unconfigured === 'true',
          device: overlay.dataset.device || 'all',
          scope: overlay.dataset.displayScope || 'all',
          frequency: overlay.dataset.frequency || '7days',
          eligible: status.eligible,
          reason: status.reason
        };
      });
    }

    /**
     * Limpieza completa de timers y listeners (usado al recargar sección en Theme Editor).
     */
    destroy() {
      if (this.activeTimer) {
        clearTimeout(this.activeTimer);
        this.activeTimer = null;
      }

      if (this.activePopupOverlay) {
        this.closePopup(this.activePopupOverlay, 'unloaded');
      }

      document.removeEventListener('keydown', this.boundKeydownHandler);
      this.root.removeEventListener('click', this.boundOverlayClickHandler);
      window.removeEventListener('message', this.boundMessageHandler);
      window.removeEventListener('resize', this.boundResizeHandler);
      document.body.classList.remove('zuhe-popup-open');
    }
  }

  // =========================================================================
  // Inicialización e Integración con Shopify Theme Editor
  // =========================================================================

  let activeControllers = [];

  function initSection() {
    const rootElements = document.querySelectorAll('.zuhe-popups-root');
    rootElements.forEach((el) => {
      // Evitar doble inicialización del mismo nodo
      if (!el.dataset.zuheInitialized) {
        el.dataset.zuheInitialized = 'true';
        const controller = new ZuhePopupsController(el);
        activeControllers.push(controller);
      }
    });
  }

  // Ejecutar al cargar DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSection);
  } else {
    initSection();
  }

  // Escucha eventos del editor de temas de Shopify (Design Mode)
  document.addEventListener('shopify:section:load', function (event) {
    const sectionRoot = event.target.querySelector('.zuhe-popups-root');
    if (sectionRoot) {
      delete sectionRoot.dataset.zuheInitialized;
      const controller = new ZuhePopupsController(sectionRoot);
      activeControllers.push(controller);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const sectionId = event.detail.sectionId;
    activeControllers = activeControllers.filter((ctrl) => {
      if (ctrl.sectionId === sectionId || ctrl.root.closest('#shopify-section-' + sectionId)) {
        ctrl.destroy();
        return false;
      }
      return true;
    });
  });

  // Previsualización interactiva de bloques en el editor
  document.addEventListener('shopify:block:select', function (event) {
    const blockId = event.detail.blockId;
    activeControllers.forEach((ctrl) => {
      const targetOverlay = ctrl.root.querySelector(`[data-block-id="${blockId}"]`);
      if (targetOverlay) {
        // Abrir inmediatamente el bloque seleccionado en el editor (sin delay ni filtros)
        ctrl.openPopup(targetOverlay, true);
      }
    });
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    const blockId = event.detail.blockId;
    activeControllers.forEach((ctrl) => {
      const targetOverlay = ctrl.root.querySelector(`[data-block-id="${blockId}"]`);
      if (targetOverlay && ctrl.activePopupOverlay === targetOverlay) {
        ctrl.closePopup(targetOverlay, 'deselected');
      }
    });
  });

  // Exponer API global dinámica para tests, diagnósticos y personalización
  window.ZuhePopups = {
    normalizePath: normalizePath,
    isSystemExcluded: isSystemExcluded,
    StorageSafe: StorageSafe,
    buildUtmUrl: buildUtmUrl,
    generateBrevoDocContent: generateBrevoDocContent,
    ZuhePopupsController: ZuhePopupsController,
    diagnose: function () {
      const allReports = activeControllers.map(c => c.diagnose());
      if (typeof console !== 'undefined' && console.table && allReports.length > 0) {
        allReports.forEach((rep, idx) => {
          console.log(`--- Reporte Controlador ${idx + 1} ---`);
          console.table(rep);
        });
      }
      return allReports;
    },
    get controllers() {
      return activeControllers;
    }
  };
})();
